/**
 * RightLayerDrawer.ts
 * Right-anchored desktop drawer for SkySkout.
 * Features:
 * - Header with Activity dropdown (canned + custom personas + Manage...) and close [✕] button.
 * - Section 1: "<Activity> Layers" 4-width desktop icon grid with mouseover tooltips and collapse chevron.
 * - Section 2: 8 standard atmospheric forecast categories with in-place expanding legend cards.
 * - Collapsible cards for effortless scrolling.
 * - Model selector and chart omitted from drawer (docked into DockedTrendChart).
 */

import { PRO_WEATHER_GROUPS, getAllActivities, MASTER_LAYER_CATALOG } from '../config';
import { ActivityPersonaId, LayerItemConfig } from '../types';
import store, { AppState } from '../state';
import TierAccessService, { getRequiredTierForLayer, TIER_RANK } from '../services/TierAccessService';

export interface RightLayerDrawerCallbacks {
  onActivityChange?: (actId: ActivityPersonaId) => void;
  onLayerToggle?: (productId: string, enabled: boolean, model?: string, product?: string) => void;
  onAboutClick?: () => void;
  onDataStatusClick?: () => void;
  renderCardBody?: (layerKey: string, state: AppState) => string;
  bindCardEvents?: (cardEl: HTMLElement, layerKey: string) => void;
}

export class RightLayerDrawer {
  private container: HTMLElement;
  private headerEl: HTMLElement;
  private personaBarEl: HTMLElement;
  private scrollBodyEl: HTMLElement;

  private activitySectionEl: HTMLElement;
  private activityHeaderEl: HTMLElement;
  private activityGridEl: HTMLElement;

  private categoriesSectionEl: HTMLElement;
  private toggleTabEl: HTMLButtonElement;

  private selectedActivity: ActivityPersonaId = 'photography';
  private isActivityGroupCollapsed: boolean = false;
  private isDrawerCollapsed: boolean = false;
  private collapsedCardKeys: Set<string> = new Set();
  private openCategoryIds: Set<string> = new Set();

  constructor(
    private parentElement: HTMLElement,
    private callbacks: RightLayerDrawerCallbacks = {}
  ) {
    // Restore saved activity
    const savedPersona = localStorage.getItem('gw_active_persona') as ActivityPersonaId;
    const allActs = getAllActivities(store.getState().customActivities);
    if (savedPersona && allActs[savedPersona]) {
      this.selectedActivity = savedPersona;
    }

    this.container = document.createElement('aside');
    this.container.id = 'gw-right-layer-drawer';
    this.container.className = 'gw-right-layer-drawer';

    // 1. Header
    this.headerEl = document.createElement('div');
    this.headerEl.className = 'gw-right-drawer-header';
    this.renderHeader();

    // 1b. Persona Pill Chips Bar
    this.personaBarEl = document.createElement('div');
    this.personaBarEl.className = 'gw-persona-chips-bar';
    this.personaBarEl.id = 'gw-drawer-persona-chips-bar';
    this.renderPersonaChips();

    // 2. Scrollable Body
    this.scrollBodyEl = document.createElement('div');
    this.scrollBodyEl.className = 'gw-right-drawer-scroll-body';

    // 2a. Activity 4-Width Launchpad Section
    this.activitySectionEl = document.createElement('div');
    this.activitySectionEl.className = 'gw-drawer-activity-section';
    this.activityHeaderEl = document.createElement('div');
    this.activityHeaderEl.className = 'gw-drawer-section-header';
    this.activityGridEl = document.createElement('div');
    this.activityGridEl.className = 'gw-activity-desktop-grid';
    this.activitySectionEl.appendChild(this.activityHeaderEl);
    this.activitySectionEl.appendChild(this.activityGridEl);

    // 2b. 8 Atmospheric Categories Section
    this.categoriesSectionEl = document.createElement('div');
    this.categoriesSectionEl.className = 'gw-drawer-categories-section';

    this.scrollBodyEl.appendChild(this.activitySectionEl);
    this.scrollBodyEl.appendChild(this.categoriesSectionEl);

    // 3. Drawer Toggle Tab (Fixed on left edge of drawer)
    this.toggleTabEl = document.createElement('button');
    this.toggleTabEl.type = 'button';
    this.toggleTabEl.className = 'gw-right-drawer-tab';
    this.toggleTabEl.title = 'Toggle Weather & Activity Layers Drawer';
    this.toggleTabEl.innerHTML = '<span>LAYERS</span> <span class="gw-tab-arrow">›</span>';
    this.toggleTabEl.onclick = (e) => {
      e.stopPropagation();
      this.toggleDrawer();
    };

    this.container.appendChild(this.headerEl);
    this.container.appendChild(this.personaBarEl);
    this.container.appendChild(this.scrollBodyEl);
    this.container.appendChild(this.toggleTabEl);

    this.parentElement.appendChild(this.container);

    this.renderActivitySection();
    this.renderCategoriesSection();
    this.setupSubscriptions();
  }

  public toggleDrawer(): void {
    this.isDrawerCollapsed = !this.isDrawerCollapsed;
    if (this.isDrawerCollapsed) {
      this.container.classList.add('collapsed');
      this.toggleTabEl.querySelector('.gw-tab-arrow')!.textContent = '‹';
    } else {
      this.container.classList.remove('collapsed');
      this.toggleTabEl.querySelector('.gw-tab-arrow')!.textContent = '›';
    }
  }

  public open(): void {
    this.isDrawerCollapsed = false;
    this.container.classList.remove('collapsed');
    this.toggleTabEl.querySelector('.gw-tab-arrow')!.textContent = '›';
  }

  public close(): void {
    this.isDrawerCollapsed = true;
    this.container.classList.add('collapsed');
    this.toggleTabEl.querySelector('.gw-tab-arrow')!.textContent = '‹';
  }

  public setSnapState(state: string): void {
    if (state === 'collapsed') {
      this.close();
    } else {
      this.open();
    }
  }

  public syncDrawerPosition(): void {
    // Always right docked on desktop
  }

  private renderHeader(): void {
    this.headerEl.innerHTML = '';

    const titleWrap = document.createElement('div');
    titleWrap.className = 'gw-right-drawer-title-wrap';
    titleWrap.innerHTML = `
      <span class="gw-drawer-title-icon">🌤️</span>
      <span class="gw-drawer-title-text">WEATHER &amp; ACTIVITY LAYERS</span>
    `;

    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'gw-right-drawer-header-actions';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'gw-right-drawer-close-btn';
    closeBtn.title = 'Close Drawer';
    closeBtn.innerHTML = '&times;';
    closeBtn.onclick = (e) => {
      e.stopPropagation();
      this.close();
    };

    actionsWrap.appendChild(closeBtn);

    this.headerEl.appendChild(titleWrap);
    this.headerEl.appendChild(actionsWrap);
  }

  private renderPersonaChips(): void {
    if (!this.personaBarEl) return;
    this.personaBarEl.innerHTML = '';

    const state = store.getState();
    const allActivities = getAllActivities(state.customActivities);
    const isPro = TIER_RANK[TierAccessService.getUserAccessTier(state)] >= TIER_RANK.pro;

    const defaultIds = ['standard_forecast', 'hiking', 'photography', 'stargazing', 'winter_sports', 'water_sports', 'advanced'];
    const rawList = isPro ? (state.enabledActivities && state.enabledActivities.length > 0 ? state.enabledActivities : defaultIds) : defaultIds;

    // Guarantee standard_forecast is never omitted so the user can always return to standard forecast
    const activeList = rawList.includes('standard_forecast') ? rawList : ['standard_forecast', ...rawList];

    for (const actId of activeList) {
      const act = allActivities[actId];
      if (!act) continue;

      const title = act.title;
      const icon = act.icon;
      const isActive = this.selectedActivity === actId;
      const isCustom = !!act.isCustom;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = `gw-persona-chip ${isCustom ? 'custom-persona' : ''} ${isActive ? 'active' : ''}`.trim();
      chip.setAttribute('data-persona', act.id);
      chip.title = isCustom ? `Switch to custom persona ${title}` : `Switch to ${title} persona`;
      chip.innerHTML = `<span class="gw-persona-chip-icon">${icon}</span><span class="gw-persona-chip-label">${title}</span>`;
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectActivity(act.id as ActivityPersonaId);
      });
      this.personaBarEl.appendChild(chip);
    }

    // Manage Activities action chip
    const manageBtn = document.createElement('button');
    manageBtn.type = 'button';
    manageBtn.className = 'gw-persona-chip gw-persona-chip-manage';
    manageBtn.title = 'Manage and curate custom activities';
    manageBtn.innerHTML = '<span class="gw-persona-chip-icon">⚙️</span><span class="gw-persona-chip-label">Manage...</span>';
    manageBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const panel = (window as any).__accountPanel;
      if (panel && typeof panel.open === 'function') {
        panel.open('activities');
      } else {
        store.update({ isProfileModalOpen: true });
      }
    });
    this.personaBarEl.appendChild(manageBtn);
  }

  public selectActivity(actId: ActivityPersonaId): void {
    this.selectedActivity = actId;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('gw_active_persona', actId);
      } catch (_) {}
    }
    store.update({ activePersona: actId });
    this.renderPersonaChips();
    this.renderActivitySection();
    if (this.callbacks.onActivityChange) {
      this.callbacks.onActivityChange(actId);
    }
  }

  private buildLockedTierBadgeHtml(layerId: string, state: AppState, compact = false): string {
    if (TierAccessService.userHasLayerAccess(layerId, state)) {
      return '';
    }
    const reqTier = getRequiredTierForLayer(layerId);
    const label = TierAccessService.getTierBadgeLabel(reqTier);
    const isPaid = TIER_RANK[reqTier] >= TIER_RANK.pro;
    const border = isPaid ? 'rgba(251, 191, 36, 0.65)' : 'rgba(56, 189, 248, 0.65)';
    const bg = isPaid ? 'rgba(120, 53, 15, 0.85)' : 'rgba(12, 74, 110, 0.85)';
    const fg = isPaid ? '#fde68a' : '#e0f2fe';
    if (compact) {
      return `<span class="gw-tier-lock-pill" aria-label="Requires ${label}" style="position: absolute; top: 3px; right: 3px; font-size: 0.52rem; font-weight: 800; line-height: 1.1; padding: 1px 4px; border-radius: 3px; background: ${bg}; color: ${fg}; border: 1px solid ${border}; letter-spacing: 0.3px; pointer-events: none;">🔒 ${label}</span>`;
    }
    return `<span class="gw-tier-lock-pill" aria-label="Requires ${label}" style="display: inline-flex; align-items: center; gap: 3px; margin-left: 6px; font-size: 0.58rem; font-weight: 800; line-height: 1.15; padding: 1px 5px; border-radius: 4px; background: ${bg}; color: ${fg}; border: 1px solid ${border}; letter-spacing: 0.35px; flex-shrink: 0;">🔒 ${label}</span>`;
  }

  // --- SECTION 1: 4-WIDTH DESKTOP ICON LAUNCHPAD ---
  private renderActivitySection(): void {
    const state = store.getState();
    const allActivities = getAllActivities(state.customActivities);
    const act = allActivities[this.selectedActivity];
    if (!act || this.selectedActivity === 'advanced') {
      this.activitySectionEl.style.display = 'none';
      return;
    }

    this.activitySectionEl.style.display = 'block';

    const primaryLayers = act.primaryLayers || act.heroLayers || [];
    const count = primaryLayers.length;

    // Header
    this.activityHeaderEl.innerHTML = `
      <div class="gw-drawer-section-title-wrap" title="${this.isActivityGroupCollapsed ? 'Expand Activity Launchpad' : 'Collapse Activity Launchpad'}">
        <span class="gw-collapse-chevron">${this.isActivityGroupCollapsed ? '▶' : '▼'}</span>
        <span class="gw-section-heading">${act.icon} ${act.title.toUpperCase()} LAYERS</span>
        <span class="gw-section-count">(${count})</span>
      </div>
    `;

    this.activityHeaderEl.onclick = (e) => {
      e.stopPropagation();
      this.isActivityGroupCollapsed = !this.isActivityGroupCollapsed;
      this.activitySectionEl.classList.toggle('collapsed', this.isActivityGroupCollapsed);
      this.activityGridEl.style.display = this.isActivityGroupCollapsed ? 'none' : 'grid';
      this.activityHeaderEl.querySelector('.gw-collapse-chevron')!.textContent = this.isActivityGroupCollapsed ? '▶' : '▼';
    };

    this.activitySectionEl.classList.toggle('collapsed', this.isActivityGroupCollapsed);
    this.activityGridEl.style.display = this.isActivityGroupCollapsed ? 'none' : 'grid';
    this.activityGridEl.innerHTML = '';

    primaryLayers.forEach((layer) => {
      const isChecked = this.checkLayerActive(layer);
      const isLocked = !TierAccessService.userHasLayerAccess(layer.id, state);
      const reqTier = getRequiredTierForLayer(layer.id);
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = `gw-desktop-icon-tile ${isChecked ? 'active' : ''}`;
      tile.style.position = 'relative';
      const baseTitle = layer.description ? `${layer.label}: ${layer.description}` : layer.label;
      tile.title = isLocked ? `${baseTitle} (${TierAccessService.getTierBadgeLabel(reqTier)} required)` : baseTitle;

      tile.innerHTML = `
        ${this.buildLockedTierBadgeHtml(layer.id, state, true)}
        <span class="gw-desktop-tile-icon">${layer.icon || '🔹'}</span>
        <span class="gw-desktop-tile-label">${layer.label}</span>
        ${isChecked ? '<span class="gw-desktop-tile-dot"></span>' : ''}
      `;

      tile.onclick = (e) => {
        e.stopPropagation();
        if (!isChecked && !TierAccessService.userHasLayerAccess(layer.id, store.getState())) {
          TierAccessService.openTierUpgradeAction(reqTier);
          return;
        }
        const newState = !isChecked;
        if (this.callbacks.onLayerToggle) {
          this.callbacks.onLayerToggle(layer.id, newState, layer.model, layer.product);
        }
        if (newState && layer.product !== 'live_weather') {
          store.update({ lastActivatedProduct: layer.product || layer.id });
        }
        if (newState) {
          // Ensure its category is open
          const cat = (layer as any).category || (MASTER_LAYER_CATALOG as any)[layer.id]?.category || (MASTER_LAYER_CATALOG as any)[layer.product || '']?.category;
          if (cat) {
            this.openCategoryIds.add(cat);
          }
          this.renderCategoriesSection();
          // Auto-scroll to layer card so the user sees controls / calendar immediately
          setTimeout(() => {
            const card = this.categoriesSectionEl.querySelector(`[data-layer="${layer.id}"]`) as HTMLElement;
            if (card) {
              card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 80);
        }
      };

      this.activityGridEl.appendChild(tile);
    });
  }

  // --- SECTION 2: 8 ATMOSPHERIC CATEGORIES WITH IN-PLACE CARDS ---
  private renderCategoriesSection(): void {
    const state = store.getState();
    const savedScrollTop = this.scrollBodyEl ? this.scrollBodyEl.scrollTop : 0;
    this.categoriesSectionEl.innerHTML = '';

    PRO_WEATHER_GROUPS.forEach((group) => {
      const groupEl = document.createElement('div');
      groupEl.className = 'gw-category-accordion-group';

      let activeCount = 0;
      group.layers.forEach((l) => {
        if (this.checkLayerActive(l)) activeCount++;
      });

      const isOpen = this.openCategoryIds.has(group.id);

      // Category Header
      const headEl = document.createElement('div');
      headEl.className = 'gw-category-accordion-head';
      headEl.innerHTML = `
        <div class="gw-category-title-wrap">
          <span class="gw-category-icon">${group.icon}</span>
          <span class="gw-category-title">${group.title}</span>
        </div>
        <div class="gw-category-right">
          ${activeCount > 0 ? `<span class="gw-category-active-badge">${activeCount} active</span>` : ''}
          <span class="gw-category-arrow">${isOpen ? '▼' : '►'}</span>
        </div>
      `;

      const bodyEl = document.createElement('div');
      bodyEl.className = 'gw-category-accordion-body';
      bodyEl.style.display = isOpen ? 'flex' : 'none';

      headEl.addEventListener('click', () => {
        const currentlyOpen = bodyEl.style.display === 'flex';
        bodyEl.style.display = currentlyOpen ? 'none' : 'flex';
        headEl.querySelector('.gw-category-arrow')!.textContent = currentlyOpen ? '►' : '▼';
        if (currentlyOpen) {
          this.openCategoryIds.delete(group.id);
        } else {
          this.openCategoryIds.add(group.id);
        }
      });

      // Render Layers inside Category (Persistent Toggle Switch + Always Visible Info Button)
      group.layers.forEach((layer) => {
        const isChecked = this.checkLayerActive(layer);
        const isCardCollapsed = this.collapsedCardKeys.has(layer.id);

        const rowEl = document.createElement('div');
        rowEl.className = `gw-category-layer-row ${isChecked ? 'active' : ''}`;
        rowEl.dataset.layer = layer.id;

        // Header Row: [▶/▼ if active] Icon Label | (i) Info | Toggle Switch
        const headRow = document.createElement('div');
        headRow.className = 'gw-layer-row-header';
        headRow.title = layer.description || layer.label;

        headRow.innerHTML = `
          <div class="gw-layer-row-left">
            ${isChecked ? `
              <button type="button" class="gw-inplace-chevron-btn" title="${isCardCollapsed ? 'Expand layer controls' : 'Collapse layer controls'}">
                ${isCardCollapsed ? '▶' : '▼'}
              </button>
            ` : ''}
            <span class="gw-category-layer-icon">${layer.icon || '🔹'}</span>
            <span class="gw-category-layer-name">${layer.label}</span>
            ${this.buildLockedTierBadgeHtml(layer.id, state, false)}
          </div>
          <div class="gw-layer-row-right">
            <button type="button" class="gw-inplace-info-btn" title="Layer Details & Documentation">ℹ️</button>
            <label class="gw-switch" onclick="event.stopPropagation()">
              <input type="checkbox" class="gw-category-toggle-input" ${isChecked ? 'checked' : ''}>
              <span class="gw-slider"></span>
            </label>
          </div>
        `;

        // Switch interaction: flipping ON enables layer; flipping OFF disables layer
        const chk = headRow.querySelector('.gw-category-toggle-input') as HTMLInputElement;
        chk.onchange = (e) => {
          e.stopPropagation();
          const targetChecked = chk.checked;
          if (targetChecked && !TierAccessService.userHasLayerAccess(layer.id, store.getState())) {
            chk.checked = false;
            TierAccessService.openTierUpgradeAction(getRequiredTierForLayer(layer.id));
            return;
          }
          if (!targetChecked) {
            this.collapsedCardKeys.delete(layer.id);
          }
          if (this.callbacks.onLayerToggle) {
            this.callbacks.onLayerToggle(layer.id, targetChecked, layer.model, layer.product);
          }
          if (targetChecked && layer.product !== 'live_weather') {
            store.update({ lastActivatedProduct: layer.product || layer.id });
          }
        };

        // Info button interaction: ALWAYS accessible next to toggle switch
        const infoBtn = headRow.querySelector('.gw-inplace-info-btn') as HTMLElement;
        infoBtn.onclick = (e) => {
          e.stopPropagation();
          const modal = (window as any).__layerDescriptionModal || (window as any).__layerDescModal;
          if (modal && typeof modal.open === 'function') {
            modal.open(layer.id);
          }
        };

        // Chevron button interaction (when active)
        const chevronBtn = headRow.querySelector('.gw-inplace-chevron-btn') as HTMLElement | null;
        if (chevronBtn) {
          chevronBtn.onclick = (e) => {
            e.stopPropagation();
            if (this.collapsedCardKeys.has(layer.id)) {
              this.collapsedCardKeys.delete(layer.id);
            } else {
              this.collapsedCardKeys.add(layer.id);
            }
            this.renderCategoriesSection();
          };
        }

        // Header click outside buttons/switches
        headRow.onclick = (e) => {
          const target = e.target as HTMLElement;
          if (target.tagName === 'INPUT' || target.closest('.gw-switch') || target.closest('.gw-inplace-info-btn')) {
            return;
          }
          if (!isChecked) {
            chk.checked = true;
            chk.dispatchEvent(new Event('change'));
          } else if (chevronBtn) {
            chevronBtn.click();
          }
        };

        rowEl.appendChild(headRow);

        // If SATELLITE and ACTIVE: Expose Cloud Deck Discrete Overlays in drawer
        const isSatLayer = (layer.id === 'satellite_imagery' || layer.id === 'live_weather_satellite' || layer.id.includes('satellite'));
        if (isChecked && isSatLayer) {
          const sublayersEl = document.createElement('div');
          sublayersEl.className = 'gw-satellite-sublayers-box';
          sublayersEl.style.cssText = 'display: flex; flex-direction: column; gap: 5px; padding: 8px 12px; margin: 4px 0 6px 0; background: rgba(15, 23, 42, 0.55); border-left: 3px solid #38bdf8; border-radius: 4px;';

          sublayersEl.innerHTML = `
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;">
              <span style="font-size: 0.65rem; font-weight: 700; text-transform: uppercase; color: #38bdf8; letter-spacing: 0.5px;">Cloud Deck Discrete Overlays</span>
              <span style="font-size: 0.60rem; color: var(--text-dim, #64748b);">Native IR / ACHA</span>
            </div>

            <!-- High Clouds (> 22,000 ft) -->
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 2px 0;">
              <label style="display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 0.74rem;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #f43f5e; flex-shrink: 0;"></span>
                <span style="color: #fca5a5; font-weight: 600;">High Clouds</span>
                <span style="font-size: 0.65rem; color: var(--text-muted);">>22k ft</span>
              </label>
              <label class="gw-switch" onclick="event.stopPropagation()" style="transform: scale(0.85); transform-origin: right center;">
                <input type="checkbox" id="drawer-sat-overlay-high" ${state.liveWeatherSatOverlayHighEnabled ? 'checked' : ''}>
                <span class="gw-slider"></span>
              </label>
            </div>

            <!-- Mid Clouds (14,500 - 22,000 ft) -->
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 2px 0;">
              <label style="display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 0.74rem;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #10b981; flex-shrink: 0;"></span>
                <span style="color: #6ee7b7; font-weight: 600;">Mid Clouds</span>
                <span style="font-size: 0.65rem; color: var(--text-muted);">14.5k-22k ft</span>
              </label>
              <label class="gw-switch" onclick="event.stopPropagation()" style="transform: scale(0.85); transform-origin: right center;">
                <input type="checkbox" id="drawer-sat-overlay-mid" ${state.liveWeatherSatOverlayMidEnabled ? 'checked' : ''}>
                <span class="gw-slider"></span>
              </label>
            </div>

            <!-- Valley Fog Sea (< 3,500 ft ASL) -->
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 2px 0;">
              <label style="display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 0.74rem;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: #38bdf8; flex-shrink: 0;"></span>
                <span style="color: #7dd3fc; font-weight: 600;">Valley Fog Sea</span>
                <span style="font-size: 0.65rem; color: var(--text-muted);">&lt;3,500 ft</span>
              </label>
              <label class="gw-switch" onclick="event.stopPropagation()" style="transform: scale(0.85); transform-origin: right center;">
                <input type="checkbox" id="drawer-sat-overlay-fog" ${state.liveWeatherSatOverlayFogEnabled ? 'checked' : ''}>
                <span class="gw-slider"></span>
              </label>
            </div>
          `;

          const highChk = sublayersEl.querySelector('#drawer-sat-overlay-high') as HTMLInputElement | null;
          if (highChk) {
            highChk.onchange = (e) => {
              e.stopPropagation();
              store.update({ liveWeatherSatOverlayHighEnabled: highChk.checked });
            };
          }
          const midChk = sublayersEl.querySelector('#drawer-sat-overlay-mid') as HTMLInputElement | null;
          if (midChk) {
            midChk.onchange = (e) => {
              e.stopPropagation();
              store.update({ liveWeatherSatOverlayMidEnabled: midChk.checked });
            };
          }
          const fogChk = sublayersEl.querySelector('#drawer-sat-overlay-fog') as HTMLInputElement | null;
          if (fogChk) {
            fogChk.onchange = (e) => {
              e.stopPropagation();
              store.update({ liveWeatherSatOverlayFogEnabled: fogChk.checked });
            };
          }

          rowEl.appendChild(sublayersEl);
        }

        // If ACTIVE and NOT collapsed: render in-place controls / legend / calendar body
        if (isChecked && !isCardCollapsed) {
          const cardBody = document.createElement('div');
          cardBody.className = 'gw-inplace-card-body';

          let bodyHtml = '';
          const cardKey = (layer.product && layer.product !== 'live_weather') ? layer.product : layer.id;
          if (this.callbacks.renderCardBody) {
            bodyHtml = this.callbacks.renderCardBody(cardKey, state);
          }

          const poi = state.selectedPoi;
          if (poi) {
            const units = state.units || 'imperial';
            const elevDisplay = units === 'metric' ? `${Math.round(poi.elev / 3.28084).toLocaleString()}m` : `${Math.round(poi.elev).toLocaleString()}ft`;
            bodyHtml += `
              <div class="gw-inplace-poi-readout">
                <span>📍 POI:</span>
                <span class="gw-poi-coords">${poi.lat.toFixed(3)}°N, ${poi.lng.toFixed(3)}°W (${elevDisplay})</span>
              </div>
            `;
          }

          cardBody.innerHTML = bodyHtml;
          rowEl.appendChild(cardBody);

          // Bind card specific controls (checkboxes, buttons)
          if (this.callbacks.bindCardEvents) {
            this.callbacks.bindCardEvents(rowEl, cardKey);
          }
        }

        bodyEl.appendChild(rowEl);
      });

      groupEl.appendChild(headEl);
      groupEl.appendChild(bodyEl);
      this.categoriesSectionEl.appendChild(groupEl);
    });

    if (this.scrollBodyEl && savedScrollTop > 0) {
      this.scrollBodyEl.scrollTop = savedScrollTop;
    }
  }

  public checkLayerActive(layerItem: LayerItemConfig): boolean {
    const state = store.getState();
    if (!layerItem) return false;
    const id = layerItem.id;
    const prod = layerItem.product;

    if (id === 'precip_radar' || id === 'live_weather_radar') return !!state.liveWeatherRadarEnabled;
    if (id === 'lightning_live' || id === 'live_weather_lightning') return !!state.liveWeatherRadarLightningEnabled;
    if (prod === 'precip_accum' || id === 'precip_accum' || id === 'rain_accum' || id === 'live_weather_qpe') {
      if (id === 'live_weather_qpe') {
        return state.liveWeatherQpeMode !== 'off';
      }
      return !!state.precipAccumEnabled || state.primaryProduct === 'precip_accum' || state.primaryProduct === 'rain_accum' || !!(state.activeLayerProducts && (state.activeLayerProducts['precip_accum'] === true || state.activeLayerProducts['rain_accum'] === true));
    }
    if (prod === 'snow_accum' || id === 'snow_accum') {
      return !!state.snowAccumEnabled || state.primaryProduct === 'snow_accum' || !!(state.activeLayerProducts && state.activeLayerProducts['snow_accum'] === true);
    }
    if (id === 'live_weather_snow') return state.liveWeatherSnowMode !== 'off';
    if (id.includes('satellite') || id === 'live_weather_satellite' || id === 'satellite_imagery') return !!state.liveWeatherSatelliteEnabled;
    if (id === 'winds_surface_current' || id === 'rtma_winds') return !!state.liveWeatherRtmaWindsEnabled;
    if (id === 'sun_intensity_actual' || (prod === 'live_weather' && (layerItem as any).model === 'sun_intensity')) {
      return !!state.liveWeatherSunIntensityEnabled || !!(state.activeLayerProducts && state.activeLayerProducts['sun_intensity_actual'] === true);
    }
    if (id === 'sun_intensity_forecast' || id === 'sun_intensity' || prod === 'sun_intensity') {
      return state.primaryProduct === 'sun_intensity' || !!(state.activeLayerProducts && (state.activeLayerProducts['sun_intensity'] === true || state.activeLayerProducts['sun_intensity_forecast'] === true));
    }
    if (id === 'hrtemp' || prod === 'hrtemp') {
      return state.primaryProduct === 'hrtemp' || !!(state.activeLayerProducts && (state.activeLayerProducts['hrtemp'] === true || state.activeLayerProducts[id] === true));
    }
    if (id === 'hrtemp_current' || prod === 'hrtemp_current') {
      return state.primaryProduct === 'hrtemp_current' || !!state.liveWeatherTempGradientEnabled || !!(state.activeLayerProducts && (state.activeLayerProducts['hrtemp_current'] === true || state.activeLayerProducts[id] === true));
    }
    if (id === 'hrtemp_contours' || prod === 'hrtemp_contours') {
      return state.primaryProduct === 'hrtemp_contours' || !!(state.activeLayerProducts && (state.activeLayerProducts['hrtemp_contours'] === true || state.activeLayerProducts[id] === true));
    }
    if (id === 'wx_stations' || prod === 'wx_stations') {
      return !!state.wxStationsEnabled || state.primaryProduct === 'wx_stations' || !!(state.activeLayerProducts && state.activeLayerProducts['wx_stations'] === true);
    }
    if (id === 'aqi_stations') return !!state.liveWeatherAqiStationsEnabled;
    if (id === 'optics_dew_risk' || prod === 'optics_dew_risk') return !!state.opticsDewRiskEnabled || state.primaryProduct === 'optics_dew_risk' || !!(state.activeLayerProducts && (state.activeLayerProducts['optics_dew_risk'] === true || state.activeLayerProducts[id] === true));
    if (id === 'bortle') return !!state.bortleOverlayEnabled;
    if (id === 'solar_lunar_tools' || id === 'solar_lunar') return !!state.solarLunarToolsEnabled;
    if (id === 'climate_data' || id === 'climate' || prod === 'climate') return !!state.climateEnabled || state.primaryProduct === 'climate' || !!(state.activeLayerProducts && (state.activeLayerProducts['climate'] === true || state.activeLayerProducts[id] === true));
    if (id === 'recent_climate' || prod === 'recent_climate' || prod === 'climatedata') return !!state.recentClimateEnabled || state.primaryProduct === 'recent_climate' || !!(state.activeLayerProducts && (state.activeLayerProducts['recent_climate'] === true || state.activeLayerProducts[id] === true));
    if (prod === 'clouds' || id === 'clouds') return !!state.cloudsEnabled || state.primaryProduct === 'clouds';
    if (prod === 'winds' || id === 'winds' || id === 'winds_surface') return !!state.windsEnabled || state.primaryProduct === 'winds';
    if (prod === 'smoke' || id === 'smoke' || id === 'smoke_forecast') return !!state.smokeEnabled || state.primaryProduct === 'smoke';
    if (prod === 'dust' || id === 'dust' || id === 'dust_forecast') return !!state.dustEnabled || state.primaryProduct === 'dust';
    if (prod === 'precip' || id === 'precip' || id === 'precip_forecast') return state.primaryProduct === 'precip' || !!(state.activeLayerProducts && (state.activeLayerProducts['precip'] === true || state.activeLayerProducts['precip_forecast'] === true));
    if (prod === 'streamflow' || id === 'streamflow' || id === 'streamflow_gauges' || id === 'water_temp') return !!state.streamflowEnabled || state.primaryProduct === 'streamflow';
    if (prod === 'snow_depth' || id === 'snow_depth') return !!state.snowDepthEnabled || state.primaryProduct === 'snow_depth' || !!(state.activeLayerProducts && state.activeLayerProducts['snow_depth'] === true);
    if (prod === 'wildfires' || id === 'wildfires') {
      return !!state.wildfiresEnabled || state.primaryProduct === 'wildfires' || !!(state.activeLayerProducts && state.activeLayerProducts['wildfires'] === true);
    }
    if (id === 'wildfire_hotspots') {
      return (state.wildfireHotspotsEnabled !== false) && (!!state.wildfiresEnabled || state.primaryProduct === 'wildfires' || !!(state.activeLayerProducts && (state.activeLayerProducts['wildfires'] === true || state.activeLayerProducts['wildfire_hotspots'] === true)));
    }
    if (prod === 'fire_restrictions' || id === 'fire_restrictions') return !!state.fireRestrictionsEnabled || state.primaryProduct === 'fire_restrictions';
    if (prod === 'avalanche' || id === 'avalanche' || id === 'avalanche_danger') return !!state.avalancheEnabled || state.primaryProduct === 'avalanche';
    if (prod === 'aurora' || id === 'aurora' || id === 'aurora_viewline' || id === 'kp_index') return !!state.auroraEnabled || state.primaryProduct === 'aurora';
    if (prod === 'foliage' || id === 'foliage') return !!state.foliageEnabled || state.primaryProduct === 'foliage';
    if (prod === 'alpine_wildflowers' || id === 'alpine_wildflowers') return !!state.alpineWildflowersEnabled || state.primaryProduct === 'alpine_wildflowers';
    if (prod === 'desert_wildflowers' || id === 'desert_wildflowers') return !!state.desertWildflowersEnabled || state.primaryProduct === 'desert_wildflowers';
    if (prod === 'wild_berries' || id === 'wild_berries') return !!state.wildBerriesEnabled || state.primaryProduct === 'wild_berries';
    if (prod === 'mushrooms' || id === 'mushrooms') return !!state.mushroomEnabled || state.primaryProduct === 'mushrooms';
    if (prod === 'cpc_outlooks' || id === 'cpc_outlook' || id === 'cpc_outlooks' || id === 'cpc_6_10' || id === 'cpc_8_14') {
      return !!state.cpcOutlookEnabled || state.primaryProduct === 'cpc_outlook' || !!(state.activeLayerProducts && (state.activeLayerProducts[id] === true || state.activeLayerProducts['cpc_outlook'] === true || state.activeLayerProducts['cpc_outlooks'] === true));
    }

    return (
      (prod && prod !== 'live_weather' && state.primaryProduct === prod) ||
      state.primaryProduct === id ||
      !!(state.activeLayerProducts && (state.activeLayerProducts[id] === true || (prod && prod !== 'live_weather' && state.activeLayerProducts[prod] === true)))
    );
  }

  private setupSubscriptions(): void {
    let lastSig = '';
    store.subscribe((state) => {
      const activeSig = JSON.stringify({
        persona: state.activePersona,
        enabledActivities: state.enabledActivities,
        uiFontSize: state.uiFontSize,
        customActivitiesCount: (state.customActivities || []).length,
        activeLayers: state.activeLayerProducts,
        primary: state.primaryProduct,
        radar: state.liveWeatherRadarEnabled,
        radarLight: state.liveWeatherRadarLightningEnabled,
        qpeMode: state.liveWeatherQpeMode,
        snowMode: state.liveWeatherSnowMode,
        sat: state.liveWeatherSatelliteEnabled,
        satMode: state.liveWeatherSatRenderMode,
        satSources: state.liveWeatherSatelliteSources,
        satSource: state.liveWeatherSatelliteSource,
        satOverlayHigh: state.liveWeatherSatOverlayHighEnabled,
        satOverlayMid: state.liveWeatherSatOverlayMidEnabled,
        satOverlayFog: state.liveWeatherSatOverlayFogEnabled,
        rtmaWinds: state.liveWeatherRtmaWindsEnabled,
        rtmaWindsMode: state.liveWeatherRtmaWindsMode,
        rtmaWindsOpacity: state.liveWeatherRtmaWindsOpacity,
        windDisplayMode: state.windDisplayMode,
        windParticlesPlaying: state.windParticlesPlaying,
        windParticlesVisible: state.windParticlesVisible,
        windParticleDensityLevel: state.windParticleDensityLevel,
        windColorMode: state.windColorMode,
        windParticleSize: state.windParticleSize,
        windTrailLength: state.windTrailLength,
        windSpeed: state.windSpeed,
        sunActual: state.liveWeatherSunIntensityEnabled,
        clouds: state.cloudsEnabled,
        cloudLevels: (state.cloudLevels || []).join(','),
        winds: state.windsEnabled,
        smoke: state.smokeEnabled,
        stream: state.streamflowEnabled,
        streamFilter: state.streamflowSiteFilter,
        streamMode: state.streamflowColorMode,
        streamTemp: state.streamflowTempMapEnabled,
        streamLabels: state.streamflowLabelsEnabled,
        snow: state.snowDepthEnabled,
        snowDepthModel: state.snowDepthModel,
        snowDepthShowStations: state.snowDepthShowStations,
        snowDepthUnits: state.snowDepthUnits,
        snowAccum: state.snowAccumEnabled,
        snowAccumMode: state.snowAccumMode,
        snowAccumHours: state.snowAccumHours,
        snowAccumDay: state.snowAccumDay,
        snowAccumModel: state.snowAccumModel,
        precipViewMode: state.precipViewMode,
        precipAccum: state.precipAccumEnabled,
        precipAccumMode: state.precipAccumMode,
        precipAccumHours: state.precipAccumHours,
        precipAccumDay: state.precipAccumDay,
        precipAccumModel: state.precipAccumModel,
        lenticularMode: state.lenticularMode,
        showLenticularWinds: state.showLenticularWinds,
        showJetStreamWinds: state.showJetStreamWinds,
        jetStreamMode: state.jetStreamDisplayMode,
        jetStreamParticlesPlaying: state.jetStreamParticlesPlaying,
        jetStreamParticlesVisible: state.jetStreamParticlesVisible,
        jetStreamParticleDensityLevel: state.jetStreamParticleDensityLevel,
        jetStreamColorMode: state.jetStreamColorMode,
        jetStreamParticleSize: state.jetStreamParticleSize,
        jetStreamTrailLength: state.jetStreamTrailLength,
        jetStreamSpeed: state.jetStreamSpeed,
        lightningViewMode: state.lightningViewMode,
        liveWeatherTempGradientEnabled: state.liveWeatherTempGradientEnabled,
        wxStationsEnabled: state.wxStationsEnabled,
        wxStationsOverlayEnabled: state.wxStationsOverlayEnabled,
        wxStationsMetric: state.wxStationsMetric,
        liveWeatherAqiStationsEnabled: state.liveWeatherAqiStationsEnabled,
        aqiMode: state.aqiMode,
        hrtempRasterEnabled: state.hrtempRasterEnabled,
        wildfireHotspots: state.wildfireHotspotsEnabled,
        foliage: state.foliageEnabled,
        foliageDate: state.foliageSelectedDate,
        foliageMonth: state.foliageMonthIndex,
        alpineWildflowers: state.alpineWildflowersEnabled,
        desertWildflowers: state.desertWildflowersEnabled,
        wildBerries: state.wildBerriesEnabled,
        wildflowerDate: state.alpineWildflowerSelectedDate || state.wildflowerSelectedDate,
        desertDate: state.desertWildflowerSelectedDate,
        berriesDate: state.wildBerrySelectedDate,
        climate: state.climateEnabled,
        climateMode: state.climateViewMode,
        climateVar: state.climateSelectedVariable,
        climateMonth: state.climateMonthIndex,
        climateDate: state.climateSelectedDate,
        climateWeek: state.climateSelectedWeek,
        recentClimate: state.recentClimateEnabled,
        recentClimateYear: state.recentClimateYear,
        recentClimateMode: state.recentClimateTimeMode,
        recentClimateMonth: state.recentClimateMonth,
        recentClimateVar: state.recentClimateVariable,
        mushrooms: state.mushroomEnabled,
        mushroomSpecies: state.mushroomSpecies,
        drought: state.droughtEnabled,
        droughtMode: state.droughtMode,
        cpcOutlook: state.cpcOutlookEnabled,
        cpcHorizon: state.cpcHorizon,
        cpcVariable: state.cpcVariable,
        alpenglowMode: state.alpenglowMode,
        selectedModels: state.selectedModels,
        poi: state.selectedPoi ? `${state.selectedPoi.lat.toFixed(3)}_${state.selectedPoi.lng.toFixed(3)}` : 'none',
        userTier: TierAccessService.getUserAccessTier(state),
        collapsedCards: Array.from(this.collapsedCardKeys).sort(),
        openCats: Array.from(this.openCategoryIds).sort()
      });

      if (activeSig !== lastSig) {
        lastSig = activeSig;
        if (state.activePersona && state.activePersona !== this.selectedActivity) {
          this.selectedActivity = state.activePersona;
        }
        this.renderPersonaChips();
        this.renderActivitySection();
        this.renderCategoriesSection();
      }
    });
  }
}

export default RightLayerDrawer;
